import toastr from 'toastr'
import MultievaluatedFormController from "./multievaluated_base_controller";
import autoComplete from "@tarekraafat/autocomplete.js";
import { useRedirectOnSuccess } from "@/src/mixins/useRedirectOnSuccess";

export default class extends MultievaluatedFormController {
  connect() {
    useRedirectOnSuccess(this);
    this.initializeAutocomplete();
    this.setupOnSave();
  }

  onItemAppended(e) {
    const appendedItemSelector = e.detail;
    const elements = document.querySelectorAll(`${appendedItemSelector} .js-companies-autocomplete`);
    if (!elements.length) {
      console.error("No elements to add autocomplete");
      return;
    }
    this.initializeAutocomplete(elements);
  }

  initializeAutocomplete(elements) {
    const root = this;
    const targets = elements ?? document.querySelectorAll(".js-companies-autocomplete:not([readonly])");
    targets.forEach(element => {
      let companySelector = null;

      element.addEventListener("init", (e) => {
        const target = e.target;
        const initialQuery = target.dataset.initialSearch || "";
        if (initialQuery.length) {
          companySelector?.start(initialQuery);
        }
      });

      companySelector = new autoComplete({
        selector: () => element,
        placeholder: "Search by company name",
        threshold: 2,
        debounce: 300,
        data: {
          src: async (query) => {
            element.classList.add("loading");
            try {
              const response = await fetch(`/api/v1/search/company_options?term=${encodeURIComponent(query)}`);
              const data = await response.json();
              data.push({ name: query, create_company: true });
              return data;
            } catch (error) {
              console.error('Error fetching companies:', error);
              return error;
            } finally {
              element.classList.remove("loading");
            }
          },
          keys: ["name"]
        },
        resultsList: {
          noResults: true,
          maxResults: 30,
          tabSelect: true
        },
        resultItem: {
          class: "autocomplete-result-item",
          highlight: true,
          element: (item, data) => {
            if (data.value.create_company) {
              item.innerHTML = `<span class="name create-company">Create company "${data.match}"</span>`;
              return;
            }
            const { project_count, status_per_market } = data.value;
            const statuses = status_per_market?.map(status => `${status.status} ${status.site.toUpperCase()} `).join(" | ") || "Not in catalog";
            item.innerHTML = `<span class="name">${data.match}</span><span class="tag tag--project">${project_count} ${project_count === 1 ? "project" : "projects"}</span> <span class="tag tag--status">${statuses}</span>`
          }
        },
        events: {
          input: {
            focus: () => {
              if (companySelector.input.value.length) companySelector.start(companySelector.input.value);
            }
          },
        }
      });

      companySelector.init();
      companySelector?.input.addEventListener("selection", root.onCompanySelected.bind(this));
    });
  }

  onCompanySelected(e) {
    const { name, id = "", website = "" } = e.detail.selection.value;
    const parent = e.target.closest(".js-item-container");

    const warning = parent.querySelector(".js-multiple-matches-warning");

    if (warning) {
      if (!document.startViewTransition) {
        warning.remove();
      } else {
        document.startViewTransition(() => warning.remove());
      }
    }

    e.target.value = name;
    parent.querySelector(".js-company-id").value = id;
    parent.querySelector(".js-company-website").value = website;
  }

  setupOnSave() {
    const saveButtons = document.querySelectorAll(".save-actions button[type=submit]");
    saveButtons.forEach(button => {
      button.addEventListener("click", (e) => {
        if (!this.validateForm()) {
          e.preventDefault();
          toastr.error("Please select the companies to proceed. If none of the options match, you can click the option to create a new company.");
          console.error("Project manufacturers: Multiple matches were found for the company name. Please select the company to continue.");
          return false;
        }
        return true;
      });
    });
  }

  validateForm() {
    const temporaryCompanyNamesInputs = document.querySelectorAll(".js-companies-autocomplete[data-initial-search]")
    if (temporaryCompanyNamesInputs.length) {
      return Array.from(temporaryCompanyNamesInputs).every(input => input.value.length);
    }
    return true;
  }
}
